import React, { useState } from 'react';
import { Row, Col, Card, Drawer, Button } from 'antd';
import { Eye, Satellite, Smartphone, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { track } from '../analytics';
import { useTranslation } from 'react-i18next';

const ValueProps = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerContent, setDrawerContent] = useState(null);
  const { t } = useTranslation();

  const props = [
    {
      icon: <Eye size={56} className="text-primary" />,
      title: t('valueProps.visTitle'),
      description: t('valueProps.visDesc'),
      gradient: "from-blue-500/10 to-primary/10",
      action: () => openDrawer('visibility')
    },
    {
      icon: <Satellite size={56} className="text-primary" />,
      title: t('valueProps.monTitle'),
      description: t('valueProps.monDesc'),
      gradient: "from-green-500/10 to-emerald-500/10",
      action: () => openDrawer('monitoring')
    },
    {
      icon: <Smartphone size={56} className="text-primary" />,
      title: t('valueProps.mobTitle'),
      description: t('valueProps.mobDesc'),
      gradient: "from-purple-500/10 to-blue-500/10",
      action: () => openDrawer('mobile')
    }
  ];

  const openDrawer = (type) => {
    setDrawerContent(type);
    setDrawerOpen(true);
    try { track('valueprops_drawer_open', { type }); } catch {}
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    try { if (drawerContent) track('valueprops_drawer_close', { type: drawerContent }); } catch {}
  };

  const renderDrawerContent = () => {
    switch (drawerContent) {
      case 'visibility':
        return (
          <div className="h-full">
            <h2 className="text-2xl font-bold mb-4">{t('valueProps.drawerVis')}</h2>
            <iframe 
              src="https://blog.agcurate.com/from-day-zero-to-delivery-what-a-fully-visible-farming-season-looks-like-with-fieldops/?utm=fieldops-landing-page-360"
              className="w-full h-full border-0 rounded-lg"
              title="360° Operational Visibility"
            />
          </div>
        );
      case 'monitoring':
        return (
          <div className="h-full">
            <h2 className="text-2xl font-bold mb-4">{t('valueProps.drawerMon')}</h2>
            <iframe 
              src="https://blog.agcurate.com/precision-starts-with-insight-how-agcurate-helps-agriops-teams-monitor-every-field-with-data-pi3?utm=fieldops-landing-page-cropmonitoring"
              className="w-full h-full border-0 rounded-lg"
              title="Satellite‑Based Crop Monitoring"
            />
          </div>
        );
      case 'mobile':
        return (
          <div className="flex flex-col items-center justify-center h-full space-y-8">
            <h2 className="text-3xl font-bold text-center">{t('valueProps.drawerMobTitle')}</h2>
            <p className="text-lg text-gray-600 text-center max-w-md">{t('valueProps.drawerMobDesc')}</p>
            <div className="flex flex-col sm:flex-row gap-6">
              <a 
                href="https://apps.apple.com/nl/app/fieldops-by-agcurate/id6478125255?l=en-GB"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => { try { track('valueprops_mobile_badge_click', { store: 'ios' }); } catch {} }}
              >
                <div className="h-16 w-56 hover:scale-105 transition-transform duration-300">
                  <img 
                    src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                    alt={t('valueProps.appStoreAlt')}
                    className="h-full w-auto object-contain"
                  />
                </div>
              </a>
              <a 
                href="https://play.google.com/store/apps/details?id=com.AgcurateFieldOpsMobile"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => { try { track('valueprops_mobile_badge_click', { store: 'android' }); } catch {} }}
              >
                <div className="h-16 w-56 hover:scale-105 transition-transform duration-300">
                  <img 
                    src="/assets/GetItOnGooglePlay_Badge_Web_color_English.png"
                    alt={t('valueProps.playStoreAlt')}
                    className="h-full w-auto object-contain"
                  />
                </div>
              </a>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="py-32 bg-gradient-to-b from-light via-white to-light relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-primary/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-green-500/5 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <h2 className="text-5xl md:text-6xl font-black mb-6 bg-gradient-to-r from-dark via-primary to-dark bg-clip-text text-transparent">{t('valueProps.sectionTitle')}</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">{t('valueProps.sectionSubtitle')}</p>
        </motion.div>

        <Row gutter={[48, 48]} justify="center">
          {props.map((prop, index) => (
            <Col xs={24} lg={8} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="h-full"
              >
                <Card 
                  className={`h-full text-center p-8 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 border-0 bg-gradient-to-br ${prop.gradient} backdrop-blur-sm group hover:-translate-y-2 cursor-pointer`}
                  bodyStyle={{ padding: 0 }}
                  onClick={prop.action}
                >
                  <div className="relative">
                    <div className="mb-8 flex justify-center">
                      <div className="p-6 bg-white rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                        {prop.icon}
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold mb-6 text-dark leading-tight">{prop.title}</h3>
                    <p className="text-gray-600 text-lg leading-relaxed">{prop.description}</p>
                    
                    {/* Decorative elements */}
                    <div className="absolute -top-2 -right-2 w-4 h-4 bg-primary/20 rounded-full"></div>
                    <div className="absolute -bottom-2 -left-2 w-3 h-3 bg-green-400/30 rounded-full"></div>
                  </div>
                </Card>
              </motion.div>
            </Col>
          ))}
        </Row>
      </div>
      
      <Drawer
        title={null}
        placement="right"
        onClose={closeDrawer}
        open={drawerOpen}
        width={window.innerWidth <= 768 ? '100%' : '50%'}
        height="100%"
        closable={false}
        bodyStyle={{ padding: 0, height: '100%' }}
        headerStyle={{ display: 'none' }}
      >
        <div className="relative h-full">
          <Button
            type="text"
            icon={<X size={24} />}
            onClick={closeDrawer}
            className="absolute top-4 right-4 z-50 bg-white/80 hover:bg-white shadow-lg rounded-full w-12 h-12 flex items-center justify-center"
          />
          <div className="h-full p-6">
            {renderDrawerContent()}
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default ValueProps;
